import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Wand2, Loader2, Sparkles, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ART_STYLES = [
  {
    id: 'anime',
    name: 'Anime',
    description: 'Japanese anime style, vibrant colours',
    prompt: 'high-quality anime art style, vibrant colors, clean lines, detailed shading, Japanese animation style',
    gradient: 'from-[#A855F7] to-[#6366F1]',
    previewImg: 'https://static.prod-images.emergentagent.com/jobs/68a70340-09fa-4157-b4a0-5d1bc6f89a9b/images/e2999d598ffd5bd306d9557bd8788ae759d309ce16c39d0207ed180dd7f62503.png',
  },
  {
    id: 'realistic',
    name: 'Realistic',
    description: 'Photorealistic, lifelike portraits',
    prompt: 'photorealistic portrait, hyperrealistic, detailed skin texture, studio lighting, 8k quality, DSLR photo',
    gradient: 'from-[#3B82F6] to-[#06B6D4]',
    previewImg: 'https://static.prod-images.emergentagent.com/jobs/68a70340-09fa-4157-b4a0-5d1bc6f89a9b/images/10522c5b5381eeeb8f3eae87dd0ddba31760994a8cb294f92120727d3555e3fa.png',
  },
  {
    id: 'fantasy',
    name: 'Fantasy',
    description: 'Epic fantasy art, magical vibes',
    prompt: 'epic fantasy art style, magical atmosphere, dramatic lighting, intricate details, concept art quality, painterly style',
    gradient: 'from-[#E11D48] to-[#F59E0B]',
    previewImg: 'https://static.prod-images.emergentagent.com/jobs/68a70340-09fa-4157-b4a0-5d1bc6f89a9b/images/29f27b3ed3107e66057c41de31a6f9090cf85bbd5008021199d9cac11b261718.png',
  },
  {
    id: 'digital',
    name: 'Digital Art',
    description: 'Modern digital illustration',
    prompt: 'modern digital illustration, clean digital art, semi-realistic, artstation trending, professional illustration',
    gradient: 'from-[#10B981] to-[#34D399]',
    previewImg: 'https://static.prod-images.emergentagent.com/jobs/68a70340-09fa-4157-b4a0-5d1bc6f89a9b/images/a3d6c54c7f4cdae958b173f9f10470dbb9550dcb841cc7957995a02f866087c1.png',
  },
  {
    id: 'comic',
    name: 'Comic',
    description: 'Western comic book style',
    prompt: 'comic book art style, bold outlines, dynamic shading, cel shading, Marvel/DC comic style, graphic novel illustration',
    gradient: 'from-[#F97316] to-[#EF4444]',
    previewImg: 'https://static.prod-images.emergentagent.com/jobs/68a70340-09fa-4157-b4a0-5d1bc6f89a9b/images/25b30a485bacfd106de140f56216d1a8a45dc89b93e87a8347bccb117b5d115f.png',
  },
  {
    id: 'dark',
    name: 'Dark & Gothic',
    description: 'Dark, moody, atmospheric art',
    prompt: 'dark gothic art style, moody atmosphere, dramatic shadows, dark palette, ethereal, haunting beauty, chiaroscuro lighting',
    gradient: 'from-[#71717A] to-[#18181B]',
    previewImg: 'https://static.prod-images.emergentagent.com/jobs/68a70340-09fa-4157-b4a0-5d1bc6f89a9b/images/d74965e77eb7c46ed3a1c1b8c06a0e8308a0b4c5d6ac9ec0d6eb01af897cd280.png',
  },
];

export default function GenerateAvatar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [selectedStyle, setSelectedStyle] = useState('anime');
  const [description, setDescription] = useState(searchParams.get('description') || '');
  const [charName, setCharName] = useState(searchParams.get('name') || '');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const returnTo = searchParams.get('returnTo') || '/create';

  const handleGenerate = async () => {
    if (!description.trim()) return;
    setGenerating(true);
    setError('');
    setGeneratedUrl('');

    const style = ART_STYLES.find(s => s.id === selectedStyle);
    const fullPrompt = `${charName ? charName + '. ' : ''}${description}. Art style: ${style.prompt}`;

    try {
      const { data } = await axios.post(`${API_URL}/api/generate-avatar`, {
        description: fullPrompt
      }, { withCredentials: true });
      setGeneratedUrl(data.url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate avatar. Try again.');
    }
    setGenerating(false);
  };

  const handleUseAvatar = () => {
    // Navigate back with the generated URL as a param
    const separator = returnTo.includes('?') ? '&' : '?';
    navigate(`${returnTo}${separator}generatedAvatar=${encodeURIComponent(generatedUrl)}`);
  };

  if (!user) { navigate('/login'); return null; }

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <div className="sticky top-12 z-40 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5 px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white" data-testid="generate-avatar-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <Wand2 className="w-5 h-5 text-[#A855F7]" />
        <h1 className="font-bold text-white text-sm">AI Avatar Generator</h1>
      </div>

      <div className="px-4 pt-4 space-y-5">
        {/* Style Selection */}
        <div>
          <h2 className="text-sm font-bold text-white mb-1" data-testid="style-heading">Choose Art Style</h2>
          <p className="text-xs text-[#71717A] mb-3">Select the visual style for your character</p>
          <div className="grid grid-cols-2 gap-2">
            {ART_STYLES.map((style) => (
              <button
                key={style.id}
                onClick={() => setSelectedStyle(style.id)}
                className={`relative p-3 rounded-xl border text-left transition-all ${
                  selectedStyle === style.id
                    ? 'border-[#A855F7]/60 bg-[#A855F7]/10 scale-[1.02]'
                    : 'border-white/5 bg-[#1A1D24] hover:bg-[#1A1D24]/80'
                }`}
                data-testid={`style-${style.id}`}
              >
                <div className="w-full aspect-[4/3] rounded-lg overflow-hidden mb-2 border border-white/5">
                  <img src={style.previewImg} alt={style.name} className="w-full h-full object-cover" />
                </div>
                <p className="text-sm font-medium text-white">{style.name}</p>
                <p className="text-[10px] text-[#71717A] mt-0.5">{style.description}</p>
                {selectedStyle === style.id && (
                  <div className="absolute top-2 right-2 w-5 h-5 bg-[#A855F7] rounded-full flex items-center justify-center">
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Character Description */}
        <div>
          <label className="text-sm font-bold text-white block mb-1">Describe Your Character</label>
          <p className="text-xs text-[#71717A] mb-2">Be specific about appearance, clothing, features</p>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., A young woman with long silver hair, piercing blue eyes, wearing a dark cloak with glowing runes..."
            className="w-full bg-[#1A1D24] text-white text-sm rounded-xl p-3 border border-white/10 focus:border-[#A855F7]/50 focus:outline-none resize-none h-24 placeholder:text-[#555]"
            maxLength={500}
            data-testid="avatar-description-input"
          />
          <p className="text-[10px] text-[#555] text-right mt-1">{description.length}/500</p>
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={generating || !description.trim()}
          className="w-full py-3 bg-gradient-to-r from-[#A855F7] to-[#6366F1] text-white font-medium rounded-xl disabled:opacity-40 transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
          data-testid="generate-btn"
        >
          {generating ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Generating... this may take a moment</span>
            </>
          ) : (
            <>
              <Wand2 className="w-5 h-5" />
              <span>Generate Avatar</span>
            </>
          )}
        </button>

        {error && (
          <div className="p-3 bg-[#E11D48]/10 border border-[#E11D48]/20 rounded-xl text-[#E11D48] text-sm" data-testid="generate-error">
            {error}
          </div>
        )}

        {/* Generated Result */}
        {generatedUrl && (
          <div className="space-y-3" data-testid="generated-result">
            <h3 className="text-sm font-bold text-white">Generated Avatar</h3>
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#1A1D24]">
              <img
                src={generatedUrl}
                alt="Generated avatar"
                className="w-full aspect-square object-cover"
                data-testid="generated-avatar-img"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="flex-1 py-2.5 bg-[#1A1D24] text-white text-sm font-medium rounded-xl border border-white/10 flex items-center justify-center gap-2 hover:bg-[#252830] transition-colors"
                data-testid="regenerate-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Regenerate
              </button>
              <button
                onClick={handleUseAvatar}
                className="flex-1 py-2.5 bg-[#A855F7] text-white text-sm font-medium rounded-xl flex items-center justify-center gap-2 hover:bg-[#A855F7]/80 transition-colors active:scale-[0.98]"
                data-testid="use-avatar-btn"
              >
                <Sparkles className="w-4 h-4" />
                Use This Avatar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
